import React, { useState, useEffect, useRef, useCallback } from 'react';
import JSZip from 'jszip'; // Commented out: Requires 'npm install jszip'
import { saveAs } from 'file-saver'; // Commented out: Requires 'npm install file-saver'

import { FileInput } from '../ui/FileInput'; // Import the FileInput component
import { FileList } from '../ui/FileList'; // Import the FileList component

// Main SrtGenerator component
export const SrtGenerator = () => {
    const [filesToProcess, setFilesToProcess] = useState([]);
    const [processingFile, setProcessingFile] = useState(null);
    const [processedFiles, setProcessedFiles] = useState([]);
    // IMPORTANT: For production, handle API token securely on a backend server.
    const [apiToken, setApiToken] = useState('');
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');

    const showCustomAlert = (message) => {
        setAlertMessage(message);
        setShowAlert(true);
    };

    const handleFilesSelected = useCallback((newFiles) => {
        if (newFiles.length === 0) {
            showCustomAlert("No files selected.");
            return;
        }
        const filesWithStatus = newFiles.map(file => ({
            file,
            id: Date.now() + Math.random(),
            status: 'pending',
            transcript: '',
            error: ''
        }));
        setFilesToProcess(prev => [...prev, ...filesWithStatus]);
    }, []);

    const startNextProcessing = useCallback(() => {
        if (!processingFile && filesToProcess.length > 0) {
            const nextFile = filesToProcess[0];
            setProcessingFile(nextFile);
            setFilesToProcess(prev => prev.slice(1));
        }
    }, [processingFile, filesToProcess]);

    useEffect(() => {
        startNextProcessing();
    }, [filesToProcess, processingFile, startNextProcessing]);

    useEffect(() => {
        const processCurrentFile = async () => {
            if (processingFile) {
                setProcessedFiles(prev => {
                    const existingIndex = prev.findIndex(f => f.id === processingFile.id);
                    if (existingIndex !== -1) {
                        const updated = [...prev];
                        updated[existingIndex] = { ...processingFile, status: 'processing' };
                        return updated;
                    } else {
                        return [...prev, { ...processingFile, status: 'processing' }];
                    }
                });

                try {
                    if (!apiToken) {
                        throw new Error("Speechmatics API Token is required to proceed with transcription.");
                    }

                    const SPEECHMATICS_BASE_URL = 'https://asr.api.speechmatics.com/v2';
                    const JOB_CREATION_URL = `${SPEECHMATICS_BASE_URL}/jobs`;

                    // Step 1: Upload the file and create a transcription job
                    const formData = new FormData();
                    formData.append('data_file', processingFile.file);
                    formData.append('config', JSON.stringify({
                        type: "transcription",
                        transcription_config: {
                            language: "en",
                            operating_point: "enhanced",
                        }
                    }));

                    const uploadResponse = await fetch(JOB_CREATION_URL, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${apiToken}`,
                        },
                        body: formData,
                    });

                    if (!uploadResponse.ok) {
                        const errorData = await uploadResponse.json();
                        throw new Error(`Failed to create Speechmatics job: ${uploadResponse.status} - ${errorData.detail || JSON.stringify(errorData)}`);
                    }

                    const jobCreationResult = await uploadResponse.json();
                    const jobId = jobCreationResult.id;

                    // Step 2: Poll for job completion
                    const pollInterval = 5000; // Poll every 5 seconds
                    let jobStatus = '';
                    let transcriptText = '';
                    let jobError = null;

                    while (jobStatus !== 'completed' && jobStatus !== 'rejected' && jobStatus !== 'failed' && jobStatus !== 'done') {
                        if (!processingFile) {
                            jobError = "Processing interrupted for current file.";
                            break;
                        }

                        await new Promise(resolve => setTimeout(resolve, pollInterval));

                        const statusResponse = await fetch(`${SPEECHMATICS_BASE_URL}/jobs/${jobId}`, {
                            method: 'GET',
                            headers: {
                                'Authorization': `Bearer ${apiToken}`,
                            },
                        });

                        if (!statusResponse.ok) {
                            const errorData = await statusResponse.json();
                            jobError = `Failed to get job status: ${statusResponse.status} - ${errorData.detail || JSON.stringify(errorData)}`;
                            break;
                        }

                        const statusResult = await statusResponse.json();
                        jobStatus = statusResult.job.status;

                        if (jobStatus === 'rejected' || jobStatus === 'failed') {
                            jobError = statusResult.job.failure_reason || 'Job failed or rejected for an unknown reason.';
                            break;
                        }
                    }

                    if (jobError) {
                        throw new Error(jobError);
                    }

                    // Step 3: Retrieve the transcript if the job completed successfully
                    const transcriptResponse = await fetch(`${SPEECHMATICS_BASE_URL}/jobs/${jobId}/transcript`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${apiToken}`,
                            'Content-Type': 'application/json'
                        },
                    });

                    if (!transcriptResponse.ok) {
                        const errorData = await transcriptResponse.json();
                        throw new Error(`Failed to retrieve transcript: ${transcriptResponse.status} - ${errorData.detail || JSON.stringify(errorData)}`);
                    }
                    debugger
                    const transcriptData = await transcriptResponse.json();
                    transcriptText = transcriptData.results.map(r => r.alternatives[0].content).join(' ');

                    setProcessedFiles(prev => prev.map(f =>
                        f.id === processingFile.id ? { ...f, status: 'completed', transcript: transcriptText, error: '' } : f
                    ));

                } catch (error) {
                    setProcessedFiles(prev => prev.map(f =>
                        f.id === processingFile.id ? { ...f, status: 'failed', error: error.message, transcript: '' } : f
                    ));
                    showCustomAlert(`Failed to process ${processingFile.file.name}: ${error.message}`);
                } finally {
                    setProcessingFile(null);
                }
            }
        };

        processCurrentFile();
    }, [processingFile, apiToken]);

    const handleDownloadAllTranscripts = async () => {
        const completedFiles = processedFiles.filter(f => f.status === 'completed' && f.transcript);

        if (completedFiles.length === 0) {
            showCustomAlert("No completed transcripts to download.");
            return;
        }

        try {
            const zip = new JSZip();
            completedFiles.forEach(fileItem => {
                const fileName = `${fileItem.file.name}.txt`;
                zip.file(fileName, fileItem.transcript);
            });

            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, "transcripts.zip");
            showCustomAlert("All completed transcripts downloaded as a ZIP file!");
        } catch (error) {
            console.error("Error generating or downloading ZIP:", error);
            showCustomAlert("Failed to create ZIP file. See console for details.");
        }

        // For environments where JSZip/file-saver cannot be directly imported (like this one),
        // you would typically need a backend service to generate and serve the ZIP file.
        // As a fallback for this environment, we'll just show an alert.
        showCustomAlert("ZIP download initiated. This functionality requires 'jszip' and 'file-saver' to be installed in your local project.");
        console.log("Would initiate ZIP download with completed transcripts:", completedFiles.map(f => f.file.name));
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 to-indigo-900 p-8 flex items-center justify-center">
            <div className="w-full max-w-4xl bg-white p-8 rounded-xl shadow-2xl border border-gray-200">
                <h1 className="text-4xl font-extrabold text-gray-900 mb-8 text-center leading-tight">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                        Batch Audio/Video Processor
                    </span>
                </h1>

                <div className="mb-8 p-6 bg-blue-50 rounded-lg border border-blue-200 shadow-inner">
                    <label htmlFor="apiToken" className="block text-blue-800 text-lg font-semibold mb-3">
                        Speechmatics API Token:
                    </label>
                    <input
                        type="text"
                        id="apiToken"
                        value={apiToken}
                        onChange={(e) => setApiToken(e.target.value)}
                        className="shadow-sm appearance-none border border-blue-300 rounded-lg w-full py-3 px-4 text-gray-800 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                        placeholder="Enter your Speechmatics API token here"
                    />
                    <p className="text-sm text-blue-600 mt-2">
                        <span className="font-medium">Important:</span> In a production application, this API token should be
                        handled securely on a backend server, not directly in client-side code.
                    </p>
                </div>

                <FileInput onFilesSelected={handleFilesSelected} />

                {filesToProcess.length > 0 && (
                    <div className="mt-8 p-6 bg-yellow-50 rounded-lg border border-yellow-200 shadow-inner">
                        <h2 className="text-xl font-bold text-yellow-800 mb-4">
                            <span className="inline-block mr-2">⏳</span> Pending Files ({filesToProcess.length}):
                        </h2>
                        <ul className="space-y-2">
                            {filesToProcess.map(item => (
                                <li key={item.id} className="text-yellow-700 text-base flex items-center">
                                    <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                                    {item.file.name}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {processingFile && (
                    <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200 shadow-inner flex items-center justify-center animate-pulse">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-4"></div>
                        <p className="text-xl font-semibold text-blue-800">
                            Processing: <span className="font-bold">{processingFile.file.name}</span>...
                        </p>
                    </div>
                )}

                {processedFiles.length > 0 && (
                    <>
                        <FileList files={processedFiles} />
                        <div className="mt-6 text-center">
                            <button
                                onClick={handleDownloadAllTranscripts}
                                disabled={processedFiles.filter(f => f.status === 'completed' && f.transcript).length === 0}
                                className={`bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-200 ease-in-out
                    ${processedFiles.filter(f => f.status === 'completed' && f.transcript).length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`
                                }
                            >
                                Download All Transcripts as .zip
                            </button>
                        </div>
                    </>
                )}

                {showAlert && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full border border-gray-300">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification</h3>
                            <p className="text-gray-700 mb-6">{alertMessage}</p>
                            <button
                                onClick={() => setShowAlert(false)}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition duration-200"
                            >
                                Got It
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}